import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Paginator } from "primereact/paginator";
import { useTranslation } from "react-i18next";
import { AdvanceFilter } from "../../M_Components/M_Table/advanceFilter/AdvanceFilter";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import TableHeader from "../tableHelpers/TableHeader";
import ColumnMenu from "../tableHelpers/ColumnMenu";
import ViewsMenu from "../tableHelpers/ViewMenu";
import { MCheckBox } from "src/M_Components";
import {
  getCellColor,
  headerCellStyle,
  cellStyle as tdStyle,
} from "../tableHelpers/TableStyleUtils";
import { useTableFilters } from "../tableHelpers/useTableFilters";
import "./ViewLaneTable.css";

/**
 * Safe numeric extractors for each quote metric.
 * They return:
 * - number when a valid finite number exists
 * - null otherwise
 */
const getBidValue = (q) => {
  if (!q) return null;
  const n = Number(q.BidValue ?? q.bidValue ?? q.Bid ?? q.bid);
  return Number.isFinite(n) ? n : null;
};

const getLoadsValue = (q) => {
  if (!q) return null;
  const n = Number(q.Loads ?? q.loads);
  return Number.isFinite(n) ? n : null;
};

const getTransitValue = (q) => {
  if (!q) return null;
  const n = Number(
    q.EstimatedTransitDays ??
    q.estimatedTransitDays ??
    q.TransitDays ??
    q.transitDays
  );
  return Number.isFinite(n) ? n : null;
};

/**
 * Quote identity / status helpers
 */
const getQuoteId = (q) => q?.QuoteId ?? q?.quoteId ?? q?.Id ?? q?.id ?? null;

/**
 * Last phase helper
 * Dont infer last phase via "currentPhaseId === TenderPhaseId".
 * Instead, we require the quote's tenderPhase to have IsLastPhase = true.
 */
const getQuoteIsLastPhase = (q) => {
  if (!q) return false;

  // If API flattens it onto the quote:
  if (q.IsLastPhase !== undefined) return !!q.IsLastPhase;
  if (q.isLastPhase !== undefined) return !!q.isLastPhase;

  // If API returns embedded TenderPhase:
  const tp = q.TenderPhase ?? q.tenderPhase ?? null;
  if (tp?.IsLastPhase !== undefined) return !!tp.IsLastPhase;
  if (tp?.isLastPhase !== undefined) return !!tp.isLastPhase;

  return false;
};

/**
 * Lane identity / text helpers (defensive)
 */
const getLaneId = (lane) =>
  lane?.LaneId ?? lane?.laneId ?? lane?.Id ?? lane?.id ?? null;

const getLaneOrigin = (lane) =>
  lane?.LaneSource ??
  lane?.laneSource ??
  lane?.Origin ??
  lane?.origin ??
  lane?.sourceLocation ??
  lane?.SourceLocation ??
  "";

const getLaneDestination = (lane) =>
  lane?.LaneDestination ??
  lane?.laneDestination ??
  lane?.Destination ??
  lane?.destination ??
  lane?.destinationLocation ??
  lane?.DestinationLocation ??
  "";

/**
 * Reads the best baseline value from laneBestMap when available.
 * This baseline MUST consider ALL quotes (accepted + unaccepted),
 * even though this view only renders accepted ones.
 *
 * IMPORTANT: This baseline should be computed server-side (laneSummaries) and passed to the table as laneBestMap.
 * FE must NOT scan all quotes to compute baselines.
 */
const bestFromLaneBestMap = (laneBestMap, laneId, metricKey) => {
  if (!(laneBestMap instanceof Map) || laneId == null) return null;

  const key = Number(laneId);
  if (!Number.isFinite(key)) return null;

  const b = laneBestMap.get(key);
  if (!b) return null;

  if (metricKey === "bid") return b.bestBidValue ?? null;
  if (metricKey === "loads") return b.bestLoadsValue ?? null;
  if (metricKey === "transit") return b.bestEtdValue ?? null;

  return null;
};

/**
 * Returns a carrier name for a quote.
 * Priority:
 *  1) carrierNameById map (when only CarrierId is present)
 *  2) Embedded string fields
 *  3) Embedded objects (Carrier.Name)
 *  4) Fallback '-'
 */
const getCarrierName = (q, carrierNameById) => {
  if (!q) return "-";

  const cid = q.CarrierId ?? q.carrierId ?? null;
  if (cid != null && carrierNameById instanceof Map) {
    const name = carrierNameById.get(cid);
    if (name) return name;
  }

  if (typeof q.Carrier === "string") return q.Carrier;
  if (typeof q.carrier === "string") return q.carrier;
  if (q.Carrier?.Name) return q.Carrier.Name;
  if (q.carrier?.Name) return q.carrier.Name;
  if (q.CarrierName) return q.CarrierName;
  if (q.carrierName) return q.carrierName;

  return "-";
};

/* ============================================================================================================
   Component: ViewLaneTable
   ------------------------------------------------------------------------------------------------------------
   Goal:
   - Display lanes as a flat list (Origin -> Destination)
   - Each lane may have multiple accepted quotes
      *Render N rows per lane, using rowSpan for:
        Origin / Destination / BestMetric
   - Allow deselecting (unaccepting) accepted quotes when rules allow it:
       * If tender is finished = cannot toggle anything
       * Spot = can toggle always (when not finished)
       * Non-Spot = can toggle only quotes from the LAST phase (TenderPhase.IsLastPhase === true)
   - BestMetric baseline must come from laneBestMap (/laneSummaries), NOT computed on FE.
   ============================================================================================================ */

export default function ViewLaneTable({
  tenderInfo, // not used directly here but kept for parity
  acceptedQuotes, // ONLY accepted quotes (used for rendering)
  lanes,
  carriers,
  onRefresh,
  metricKey = "bid",

  // toggle rules
  isFinished = false,
  isSpotTender = false,
  currentPhaseId = null, // kept for parity (no longer used in Non-Spot rule)

  // baseline best map computed at page level from ALL quotes (server-side summary)
  laneBestMap,

  // toggle infra shared with other views
  acceptBusyIds,
  onSetQuoteAccepted,
}) {
  const { t } = useTranslation();

  /* ---------------------------------------------------------------------------------------
     Fast lookup maps (built once per dataset):
     - carrierNameById: O(#carriers)
     - acceptedQuotesByLaneId: O(#acceptedQuotes)
     These replace repeated .find/.filter patterns that would become O(lanes × quotes).
     --------------------------------------------------------------------------------------- */
  const carrierNameById = useMemo(() => {
    const map = new Map();
    for (const c of carriers || []) {
      const id = c?.CarrierId ?? c?.Id ?? c?.carrierId ?? c?.id ?? null;
      if (id == null) continue;
      const name =
        c?.CarrierName ?? c?.Name ?? c?.carrierName ?? c?.name ?? null;
      if (name) map.set(id, name);
    }
    return map;
  }, [carriers]);

  const acceptedQuotesByLaneId = useMemo(() => {
    const map = new Map();
    for (const q of acceptedQuotes || []) {
      const laneIdRaw = q?.LaneId ?? q?.laneId ?? q?.LaneID ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (laneId == null || !Number.isFinite(laneId)) continue;

      if (!map.has(laneId)) map.set(laneId, []);
      map.get(laneId).push(q);
    }
    return map;
  }, [acceptedQuotes]);

  /* ---------------------------------------------------------------------------------------
     Pagination (IMPORTANT):
     - Pagination is performed by LANES (not rows).
     - A lane can expand into multiple <tr> entries if it has multiple accepted quotes.
     --------------------------------------------------------------------------------------- */
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  /* ---------------------------------------------------------------------------------------
     Menu anchors for existing shared UI components
     --------------------------------------------------------------------------------------- */
  const [anchorAF, setAnchorAF] = useState(null);
  const [anchorEls, setAnchorEls] = useState(null);
  const [anchorV, setAnchorV] = useState(null);

  /* ---------------------------------------------------------------------------------------
     Filters / Views
     --------------------------------------------------------------------------------------- */
  const {
    metaPair,
    views,
    addMetaPair,
    deleteFilter,
    deleteAllFilters,
    submitFilters,
    resetToDefault,
    applyView,
    saveView,
  } = useTableFilters({
    // When applying filters, reset pagination so the user always sees results.
    onSubmit: () => setFirst(0),
  });

  /* ---------------------------------------------------------------------------------------
     Busy helper:
     Accept/unaccept operations are shared across tables,
     so we disable checkboxes while an operation is in-flight.
     --------------------------------------------------------------------------------------- */
  const isBusy = useCallback(
    (quoteId) => {
      if (!quoteId) return false;
      if (!acceptBusyIds) return false;
      if (acceptBusyIds instanceof Set) return acceptBusyIds.has(quoteId);
      if (Array.isArray(acceptBusyIds)) return acceptBusyIds.includes(quoteId);
      return false;
    },
    [acceptBusyIds]
  );

  /* ---------------------------------------------------------------------------------------
     Rule engine: can the user toggle this quote in this view?
     --------------------------------------------------------------------------------------- */
  const canToggleQuote = useCallback(
    (quote) => {
      if (!quote) return false;

      // If tender is finished, selection is locked
      if (isFinished) return false;

      // Spot tender: can always toggle (when not finished)
      if (isSpotTender) return true;

      // Non-Spot: only allow toggle for quotes from the LAST phase
      return getQuoteIsLastPhase(quote) === true;
    },
    [isFinished, isSpotTender]
  );

  /* ---------------------------------------------------------------------------------------
     Toggle handler:
     We call the page-provided handler so:
     - server updates happen in one place
     - all views refresh consistently
     --------------------------------------------------------------------------------------- */
  const toggleQuoteAccepted = useCallback(
    ({ quoteId, nextAccepted }) => {
      if (!quoteId) return;
      if (typeof onSetQuoteAccepted === "function") {
        onSetQuoteAccepted({ quoteId, nextAccepted });
      }
    },
    [onSetQuoteAccepted]
  );

  /* ---------------------------------------------------------------------------------------
     Advanced filter metadata (Origin / Destination)
     --------------------------------------------------------------------------------------- */
  const metaType = useMemo(
    () => [
      { metaName: "origin", metaDisplay: "Origin", metaType: "string" },
      { metaName: "destination", metaDisplay: "Destination", metaType: "string" },
    ],
    []
  );

  /* ---------------------------------------------------------------------------------------
     Apply filters to LANES (not rows)
     NOTE: This is a UI-only search filter. If you want server-side filtering, we can add BE filters later.
     --------------------------------------------------------------------------------------- */
  const filteredLanes = useMemo(() => {
    let data = [...(lanes || [])];

    for (const f of metaPair) {
      const val = f.value?.toLowerCase();
      if (!val) continue;

      if (f.name === "origin") {
        data = data.filter((l) =>
          (getLaneOrigin(l) || "").toLowerCase().includes(val)
        );
      }

      if (f.name === "destination") {
        data = data.filter((l) =>
          (getLaneDestination(l) || "").toLowerCase().includes(val)
        );
      }
    }

    return data;
  }, [lanes, metaPair]);

  /* ---------------------------------------------------------------------------------------
     Pagination slice (still lanes)
     --------------------------------------------------------------------------------------- */
  const pagedLanes = useMemo(
    () => filteredLanes.slice(first, first + rows),
    [filteredLanes, first, rows]
  );

  /* ---------------------------------------------------------------------------------------
     Excel export:
     Requirements:
     - Must be consistent with the table layout when there are multiple accepted quotes per lane.
     - simulate rowSpan by:
       * First quote line: includes Origin/Destination/BestMetric
       * Subsequent quote lines: Origin/Destination/BestMetric are left blank
     - If a lane has no accepted quotes:
       * One line with Origin/Destination/BestMetric and '-' for quote values
     --------------------------------------------------------------------------------------- */
  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("ViewLaneTable");

    const metricLabelKey =
      metricKey === "bid"
        ? "BidValue"
        : metricKey === "loads"
          ? "Loads"
          : "EstimatedTransitDays";

    const headerRow = [
      t("Origin"),
      t("Destination"),
      t("BestMetric"),
      t("BidValue"),
      t("Loads"),
      t("EstimatedTransitDays"),
      t("Carrier"),
      t("Select Quote") || "Select Quote",
    ];

    sheet.addRow([t(metricLabelKey), ...Array(headerRow.length - 1).fill("")]);
    sheet.addRow(headerRow);

    filteredLanes.forEach((lane) => {
      const laneIdRaw = getLaneId(lane);
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) return;

      const origin = getLaneOrigin(lane) || "-";
      const destination = getLaneDestination(lane) || "-";
      const bestValue = bestFromLaneBestMap(laneBestMap, laneId, metricKey);

      const laneQuotes = acceptedQuotesByLaneId.get(laneId) || [];

      if (!laneQuotes.length) {
        sheet.addRow([
          origin,
          destination,
          bestValue ?? "-",
          "-",
          "-",
          "-",
          "-",
          "No",
        ]);
        return;
      }

      laneQuotes.forEach((q, idx) => {
        const bid = getBidValue(q);
        const loads = getLoadsValue(q);
        const transit = getTransitValue(q);
        const carrier = getCarrierName(q, carrierNameById);
        const selected = "Yes";

        sheet.addRow([
          idx === 0 ? origin : "",
          idx === 0 ? destination : "",
          idx === 0 ? bestValue ?? "-" : "",
          bid ?? "-",
          loads ?? "-",
          transit ?? "-",
          carrier ?? "-",
          selected,
        ]);
      });
    });

    try {
      sheet.getRow(2).font = { bold: true };
    } catch {
      // noop
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "FinishedViewLanes.xlsx"
    );
  };

  /* ---------------------------------------------------------------------------------------
     Columns:
     We keep column visibility toggles exactly like your other tables.
     --------------------------------------------------------------------------------------- */
  const allColumns = [
    "Origin",
    "Destination",
    "BestMetric",
    "Bid",
    "Loads",
    "EstimatedTransitDays",
    "Carrier",
    "SelectQuote",
  ];

  const [selectedColumns, setSelectedColumns] = useState(allColumns);

  return (
    <div className="viewLaneTable-wrapper">
      <TableHeader
        filterCount={metaPair.length}
        onRefresh={onRefresh}
        onExport={exportExcel}
        onOpenViews={(e) => setAnchorV(e.currentTarget)}
        onOpenFilters={(e) => setAnchorAF(e.currentTarget)}
        onOpenColumns={(e) => setAnchorEls(e.currentTarget)}
      />

      <ColumnMenu
        anchorEl={anchorEls}
        open={!!anchorEls}
        onClose={() => setAnchorEls(null)}
        singleColumns={allColumns}
        selectedColumns={selectedColumns}
        onChangeSelected={setSelectedColumns}
        columnGroups={[]}
      />

      <ViewsMenu
        anchorEl={anchorV}
        open={!!anchorV}
        onClose={() => setAnchorV(null)}
        viewsMap={views}
        onSelectDefault={() => {
          resetToDefault();
          setAnchorV(null);
        }}
        onSelectView={(view) => {
          applyView(view);
          setAnchorV(null);
        }}
        onCreateNewViewClick={() => {
          setAnchorV(null);
          setAnchorAF(document.getElementById("advFilter"));
        }}
      />

      <AdvanceFilter
        metaPair={metaPair}
        metaType={metaType}
        onAddMetaPair={addMetaPair}
        onSubmitFilters={submitFilters}
        onDeleteFilter={deleteFilter}
        onDeleteAllFilters={deleteAllFilters}
        anchorAF={anchorAF}
        setAnchorAF={setAnchorAF}
        onSaveFilter={(viewName) => saveView(viewName)}
        views={views}
        onToasts={() => { }}
      />

      <div className="viewLaneTable-table-container">
        <table className="viewLaneTable">
          <thead>
            <tr>
              {selectedColumns.includes("Origin") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-origin"
                >
                  {t("Origin")}
                </th>
              )}

              {selectedColumns.includes("Destination") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-destination"
                >
                  {t("Destination")}
                </th>
              )}

              {selectedColumns.includes("BestMetric") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-best"
                >
                  {t("BestMetric")}
                </th>
              )}

              {selectedColumns.includes("Bid") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-bid"
                >
                  {t("BidValue")}
                </th>
              )}

              {selectedColumns.includes("Loads") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-loads"
                >
                  {t("Loads")}
                </th>
              )}

              {selectedColumns.includes("EstimatedTransitDays") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-transit"
                >
                  {t("EstimatedTransitDays")}
                </th>
              )}

              {selectedColumns.includes("Carrier") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-carrier"
                >
                  {t("Carrier")}
                </th>
              )}

              {!isFinished && selectedColumns.includes("SelectQuote") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-select"
                >
                  {t("Select Quote") || "Select Quote"}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {pagedLanes.map((lane) => {
              const laneIdRaw = getLaneId(lane);
              const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
              if (!Number.isFinite(laneId)) return null;

              const origin = getLaneOrigin(lane) || "-";
              const destination = getLaneDestination(lane) || "-";

              const bestValue = bestFromLaneBestMap(
                laneBestMap,
                laneId,
                metricKey
              );

              const bestBg =
                bestValue != null
                  ? getCellColor(bestValue, bestValue)
                  : "var(--color-grey-light)";

              const laneQuotes = acceptedQuotesByLaneId.get(laneId) || [];

              if (!laneQuotes.length) {
                return (
                  <tr key={`lane-${laneId}`}>
                    {selectedColumns.includes("Origin") && (
                      <td
                        className="viewLaneTable-cell col-origin"
                        style={tdStyle({
                          background: "var(--color-grey-light)",
                          fontWeight: "bold",
                        })}
                      >
                        {origin}
                      </td>
                    )}

                    {selectedColumns.includes("Destination") && (
                      <td
                        className="viewLaneTable-cell col-destination"
                        style={tdStyle({
                          background: "var(--color-grey-light)",
                          fontWeight: "bold",
                        })}
                      >
                        {destination}
                      </td>
                    )}

                    {selectedColumns.includes("BestMetric") && (
                      <td
                        className="viewLaneTable-cell col-best"
                        style={tdStyle({ background: bestBg })}
                      >
                        {bestValue != null ? bestValue : "-"}
                      </td>
                    )}

                    {selectedColumns.includes("Bid") && (
                      <td
                        className="viewLaneTable-cell col-bid"
                        style={tdStyle()}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("Loads") && (
                      <td
                        className="viewLaneTable-cell col-loads"
                        style={tdStyle()}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("EstimatedTransitDays") && (
                      <td
                        className="viewLaneTable-cell col-transit"
                        style={tdStyle()}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("Carrier") && (
                      <td
                        className="viewLaneTable-cell col-carrier"
                        style={tdStyle()}
                      >
                        -
                      </td>
                    )}

                    {!isFinished && selectedColumns.includes("SelectQuote") && (
                      <td
                        className="viewLaneTable-cell col-select"
                        style={tdStyle()}
                      >
                        <MCheckBox
                          label={null}
                          checked={false}
                          disabled={true}
                          noPadding={true}
                          fontSize={"22px"}
                          onChange={() => { }}
                        />
                      </td>
                    )}
                  </tr>
                );
              }

              return laneQuotes.map((q, idx) => {
                const bid = getBidValue(q);
                const loads = getLoadsValue(q);
                const transit = getTransitValue(q);
                const carrier = getCarrierName(q, carrierNameById);

                const activeMetricValue =
                  metricKey === "bid"
                    ? bid
                    : metricKey === "loads"
                      ? loads
                      : transit;

                let metricBg;
                if (bestValue != null && activeMetricValue != null) {
                  metricBg =
                    metricKey === "loads"
                      ? getCellColor(bestValue, activeMetricValue)
                      : getCellColor(activeMetricValue, bestValue);
                }

                const quoteId = getQuoteId(q);
                const canToggle = canToggleQuote(q);
                const disabled = !quoteId || !canToggle || isBusy(quoteId);

                const tooltip = isFinished
                  ? t("TenderFinished")
                  : !isSpotTender && !canToggle
                    ? t("Cannot unselect quotes from previous phases.") ||
                    "Cannot unselect quotes from previous phases."
                    : "";

                return (
                  <tr key={`lane-${laneId}-q-${quoteId ?? idx}`}>
                    {idx === 0 && selectedColumns.includes("Origin") && (
                      <td
                        rowSpan={laneQuotes.length}
                        className="viewLaneTable-cell col-origin"
                        style={tdStyle({
                          background: "var(--color-grey-light)",
                          fontWeight: "bold",
                        })}
                      >
                        {origin}
                      </td>
                    )}

                    {idx === 0 && selectedColumns.includes("Destination") && (
                      <td
                        rowSpan={laneQuotes.length}
                        className="viewLaneTable-cell col-destination"
                        style={tdStyle({
                          background: "var(--color-grey-light)",
                          fontWeight: "bold",
                        })}
                      >
                        {destination}
                      </td>
                    )}

                    {idx === 0 && selectedColumns.includes("BestMetric") && (
                      <td
                        rowSpan={laneQuotes.length}
                        className="viewLaneTable-cell col-best"
                        style={tdStyle({ background: bestBg })}
                      >
                        {bestValue != null ? bestValue : "-"}
                      </td>
                    )}

                    {selectedColumns.includes("Bid") && (
                      <td
                        className="viewLaneTable-cell col-bid"
                        style={tdStyle({
                          background:
                            metricKey === "bid" ? metricBg : undefined,
                        })}
                      >
                        {bid ?? "-"}
                      </td>
                    )}

                    {selectedColumns.includes("Loads") && (
                      <td
                        className="viewLaneTable-cell col-loads"
                        style={tdStyle({
                          background:
                            metricKey === "loads" ? metricBg : undefined,
                        })}
                      >
                        {loads ?? "-"}
                      </td>
                    )}

                    {selectedColumns.includes("EstimatedTransitDays") && (
                      <td
                        className="viewLaneTable-cell col-transit"
                        style={tdStyle({
                          background:
                            metricKey === "transit" ? metricBg : undefined,
                        })}
                      >
                        {transit ?? "-"}
                      </td>
                    )}

                    {selectedColumns.includes("Carrier") && (
                      <td
                        className="viewLaneTable-cell col-carrier"
                        style={tdStyle()}
                      >
                        {carrier ?? "-"}
                      </td>
                    )}

                    {!isFinished && selectedColumns.includes("SelectQuote") && (
                      <td
                        className="viewLaneTable-cell col-select"
                        style={tdStyle()}
                      >
                        <MCheckBox
                          label={null}
                          checked={true}
                          disabled={disabled}
                          tooltipName={tooltip}
                          noPadding={true}
                          fontSize={"22px"}
                          onChange={(e) => {
                            const nextAccepted = !!e?.target?.checked;
                            if (nextAccepted === false) {
                              toggleQuoteAccepted({
                                quoteId,
                                nextAccepted: false,
                              });
                            }
                          }}
                        />
                      </td>
                    )}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      <Paginator
        first={first}
        rows={rows}
        totalRecords={filteredLanes.length}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(e) => {
          setFirst(e.first);
          setRows(e.rows);
        }}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      />
    </div>
  );
}

ViewLaneTable.propTypes = {
  tenderInfo: PropTypes.object,
  // Rendering list: accepted quotes only
  acceptedQuotes: PropTypes.array.isRequired,
  lanes: PropTypes.array.isRequired,
  carriers: PropTypes.array.isRequired,
  onRefresh: PropTypes.func.isRequired,
  metricKey: PropTypes.oneOf(["bid", "loads", "transit"]),
  // Toggle rules
  isFinished: PropTypes.bool,
  isSpotTender: PropTypes.bool,
  currentPhaseId: PropTypes.number,
  // Baseline map computed outside from ALL quotes (server summary)
  laneBestMap: PropTypes.instanceOf(Map),
  // Busy + toggle handler from page
  acceptBusyIds: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  onSetQuoteAccepted: PropTypes.func,
};
